"use client";
import React, { useState, memo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import Link from "next/link";

const stages = [
  {
    age: "20-30",
    himgleft: "/next_assets/images/financial-planning/journey-1-img-left.png",
    himgright: "/next_assets/images/financial-planning/journey-1-img-right.png",
    icon: "/next_assets/images/financial-planning/journey-1.svg",
    desc: "Your Superpower? Time.",
    inst: "₹3,000/month.",
    corp: "₹1CR. + Corpus",
  },
  {
    age: "30-40",
    himgleft: "/next_assets/images/financial-planning/journey-2-img-left.png",
    himgright: "/next_assets/images/financial-planning/journey-2-img-right.png",
    icon: "/next_assets/images/financial-planning/journey-2.svg",
    desc: "Still Time to Get Ahead",
    inst: "₹8,000/month",
    corp: "₹80 Lacs. + Corpus",
  },
  {
    age: "40-50",
    himgleft: "/next_assets/images/financial-planning/journey-3-img-left.png",
    himgright: "/next_assets/images/financial-planning/journey-3-img-right.png",
    icon: "/next_assets/images/financial-planning/journey-3.svg",
    desc: "Mid-Career? Not Mid-Way",
    inst: "₹15,000/month",
    corp: "₹60 Lacs. + Corpus",
  },
  {
    age: "50-60",
    himgleft: "/next_assets/images/financial-planning/journey-4-img-left.png",
    himgright: "/next_assets/images/financial-planning/journey-4-img-right.png",
    icon: "/next_assets/images/financial-planning/journey-4.svg",
    desc: "Closer Than You Think",
    inst: "₹25,000/month",
    corp: "₹35 Lacs. + Corpus",
  },
  {
    age: "60+",
    himgleft: "/next_assets/images/financial-planning/journey-5-img-left.png",
    himgright: "/next_assets/images/financial-planning/journey-5-img-right.png",
    icon: "/next_assets/images/financial-planning/journey-5.svg",
    desc: "Retired? Keep Planning",
    inst: "Focus on Preservation",
    corp: "Legacy Wealth",
  },
];

const StageItem = memo(function StageItem({
  stage,
  index,
  hoverIndex,
  setHoverIndex,
  isMobile991,
  isMobile550,
}: {
  stage: (typeof stages)[0];
  index: number;
  hoverIndex: number | null;
  setHoverIndex: (i: number | null) => void;
  isMobile991: boolean;
  isMobile550: boolean;
}) {
  const isHovered = hoverIndex === index;
  const hasHover = hoverIndex !== null;
  const zIndexClass = isHovered ? "z-30" : hasHover ? "z-10" : "z-20";
  const [imgHover, setImgHover] = useState<"left" | "right" | null>(null);

  const topCardVariants = isMobile550
    ? {
        initial: { opacity: 0, y: 20, scale: 0.85, rotate: -3 },
        animate: { opacity: 1, y: -110, scale: 1, rotate: 0 },
        exit: { opacity: 0, y: 20, scale: 0.9, rotate: 3 },
      }
    : isMobile991
      ? {
          initial: { opacity: 0, y: 20, scale: 0.9, rotate: -3 },
          animate: { opacity: 1, y: -50, scale: 1, rotate: 0 },
          exit: { opacity: 0, y: 20, scale: 0.9, rotate: 3 },
        }
      : {
          initial: { opacity: 0, y: 40, scale: 0.9, rotate: -3 },
          animate: { opacity: 1, y: -100, scale: 1, rotate: 0 },
          exit: { opacity: 0, y: 40, scale: 0.9, rotate: 3 },
        };

  const bottomCardVariants = isMobile550
    ? {
        initial: { opacity: 0, y: -10, scale: 0.85, rotate: 2 },
        animate: { opacity: 1, y: 80, scale: 1, rotate: 0 },
        exit: { opacity: 0, y: -10, scale: 0.85, rotate: -2 },
      }
    : isMobile991
      ? {
          initial: { opacity: 0, y: -20, scale: 0.9, rotate: 3 },
          animate: { opacity: 1, y: 100, scale: 1, rotate: 0 },
          exit: { opacity: 0, y: -20, scale: 0.9, rotate: -3 },
        }
      : {
          initial: { opacity: 0, y: -40, scale: 0.9, rotate: 3 },
          animate: { opacity: 1, y: 130, scale: 1, rotate: 0 },
          exit: { opacity: 0, y: -40, scale: 0.9, rotate: -3 },
        };

  return (
    <div
      onMouseEnter={() => setHoverIndex(index)}
      onMouseLeave={() => setHoverIndex(null)}
      className={`relative flex flex-col items-center cursor-pointer transition-all duration-200 ${zIndexClass}`}
    >
      <motion.div
        whileHover={{ zIndex: 50 }}
        transition={{ type: "spring", stiffness: 260, damping: 20 }}
        className="relative flex flex-col items-center"
      >
        <Image
          src={stage.icon}
          alt="icon"
          width={70}
          height={80}
          className="w-10 h-10 2md:w-20 2md:h-20 2xl:w-17.5 2xl:h-20 z-20"
        />
        <p className="border-2 border-[#D9D9D9] py-1 2md:py-2 px-2 2md:px-4 rounded-full bg-white mt-2 text-sm 2md:text-base font-medium text-black">
          Age: {stage.age}
        </p>
      </motion.div>

      <AnimatePresence>
        {isHovered && (
          <>
            <motion.div
              variants={topCardVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ type: "spring", stiffness: 200, damping: 15 }}
              className="absolute xxs:-top-16 xxs:w-48 2md:w-56"
            >
              <div className="flex relative w-150 xxs:w-full">
                <div
                  className="absolute w-full h-32.5 xxs:h-34 2md:h-200 inset-0 z-30 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(255, 255, 255, 0) 48.08%, #FFFFFF 100%)",
                  }}
                />
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  onMouseEnter={() => setImgHover("left")}
                  onMouseLeave={() => setImgHover(null)}
                  className={`absolute left-0 transition-all duration-200 ${imgHover === "left" ? "z-20" : "z-10"}`}
                >
                  <Image
                    src={stage.himgleft}
                    alt="left-img"
                    width={148}
                    height={187}
                    className="w-100 xxs:w-27.5 h-30 xxs:h-32.5 2md:w-32.5 2md:h-40 2xl:w-37 2xl:h-46.75"
                  />
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  onMouseEnter={() => setImgHover("right")}
                  onMouseLeave={() => setImgHover(null)}
                  className={`absolute right-0 transition-all duration-200 ${imgHover === "right" ? "z-20" : "z-10"}`}
                >
                  <Image
                    src={stage.himgright}
                    alt="right-img"
                    width={148}
                    height={187}
                    className="w-100 xxs:w-27.5 h-30 xxs:h-32.5 2md:w-32.5 2md:h-40 2xl:w-37 2xl:h-46.75"
                  />
                </motion.div>
              </div>
            </motion.div>

            <motion.div
              variants={bottomCardVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ type: "spring", stiffness: 200, damping: 15 }}
              className="absolute w-40 xxs:w-56 z-40 text-center"
            >
              <p className="text-sm xxs:text-base 2md:text-xl text-black font-bold mt-2">
                {stage.desc}
              </p>
              <p className="text-sm 2md:text-base text-primaryBlue font-semibold mt-2">
                {stage.inst}
              </p>
              <p className="text-sm xxs:text-base 2md:text-xl text-[#1AAC00] font-bold mt-2">
                {stage.corp}
              </p>
              <Link
                href={`/financial-planning/retirement-planning/${index < 3 ? "pre-retirement" : "post-retirement"}`}
                className="bg-[#003D6C] block w-fit mx-auto rounded-full py-2 2md:py-3.5 px-5 2md:px-10 text-white text-sm xxs:text-base font-semibold mt-2 shine-button-hover"
              >
                Start Planning
              </Link>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
});

export default function RetirementJourney() {
  const [hoverIndex, setHoverIndex] = useState<number | null>(0);
  const [isMobile991, setIsMobile991] = useState(false);
  const [isMobile550, setIsMobile550] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile991(window.innerWidth <= 991);
      setIsMobile550(window.innerWidth <= 550);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 150) setHoverIndex(null);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Kalam:wght@400;700&display=swap');`}</style>

      <section className="mb-17.5 xxs:mb-100 2md:mb-20 2xl:mb-100 text-center">
        <h2 className="text-[24px] leading-8 2md:text-2.5xl 2md:leading-9 2xl:text-[32px] 2xl:leading-10 font-semibold text-primaryBlack mb-4">
          Plan Your Retirement Journey With Customized Planning
        </h2>
        <p className="2md:text-lg 2xl:text-xl font-normal text-primaryBlack mb-8">
          Life changes and so do your retirement needs. Start your retirement
          plan now and shape the experiences, security and freedom you enjoy
          tomorrow.
        </p>

        <div className="relative w-full border border-black rounded-3xl shadow-[0px_4px_30px_rgba(0,0,0,0.1)] xxs:p-8 2md:p-10 bg-white overflow-hidden">
          <p className="font-[Kalam] text-base pt-4 xxs:pt-0 px-6 xxs:px-0 xxs:text-2xl xl:text-3xl font-bold text-primaryBlack text-center mb-14 xxs:mb-16 2md:mb-38">
            See how your thoughtful retirement planning can adapt at every stage
            of your life journey.
          </p>

          {hoverIndex !== null && (
            <div className="absolute inset-0 backdrop-blur-sm bg-white/40 z-20 pointer-events-none" />
          )}

          <div className="relative mb-10 xxs:mb-12 2md:mb-20 2xl:mb-32 px-5">
            <span className="inline-block w-3.5 h-3.5 absolute top-[19%] xxs:top-14 2md:top-25.75 left-[4%] xxs:left-0 rounded-full bg-[#D9D9D9]" />

            <div className="xxs:flex 2md:hidden absolute xxs:left-0 right-[6%] top-16 -translate-y-1/2 border-2 border-dashed border-[#D9D9D9] z-0 w-[86%] xxs:w-full" />

            <div className="xxs:flex 2md:hidden absolute top-30 xxs:top-34.5 right-[6%] xxs:right-0 -translate-y-1/2 border-2 border-dashed border-[#D9D9D9] z-0 h-[38%] xxs:h-[40%] xs:h-[64%]" />

            <div className="flex xs:hidden 2md:hidden absolute xxs:left-0 right-[6%] top-44 xxs:top-52.5 xs:top-21 -translate-y-1/2 border-2 border-dashed border-[#D9D9D9] z-0 w-[87%] xxs:w-full" />

            <div className="flex xs:hidden 2md:hidden absolute top-58 xxs:top-70.75 left-[6%] xxs:left-0 -translate-y-1/2 border-2 border-dashed border-[#D9D9D9] z-0 h-[37%] xxs:h-[40%]" />

            <div className="absolute left-[6%] xxs:left-0 xs:left-[20%] 2md:left-0 2xl:right-8 bottom-2.5 2md:bottom-4.5 -translate-y-1/2 border-2 border-dashed border-[#D9D9D9] z-0 w-[60%] xxs:w-[80%] 2md:w-full" />

            <span className="inline-block w-3.5 h-3.5 absolute bottom-1.5 2md:bottom-3.75 right-[30%] xxs:right-[18%] xs:right-0 xs:left-[20%] 2md:left-full 2md:right-0 rounded-full bg-[#D9D9D9]" />

            <div className="flex flex-wrap gap-x-20 xxs:gap-x-28 gap-8 xxs:gap-16 2md:gap-10 justify-center 2md:flex-nowrap 2md:justify-between items-center relative 2md:px-10">
              {stages.map((stage, i) => (
                <StageItem
                  key={i}
                  stage={stage}
                  index={i}
                  hoverIndex={hoverIndex}
                  setHoverIndex={setHoverIndex}
                  isMobile991={isMobile991}
                  isMobile550={isMobile550}
                />
              ))}
            </div>
          </div>

          <div className="bg-[#CBE0F1] rounded-bl-3xl rounded-br-3xl xxs:rounded-6xl px-4 xxs:px-5 2md:px-10 py-4 xxs:py-5 flex flex-col xxs:flex-row justify-between items-center">
            <div className="text-center xxs:text-left w-full xxs:w-2/3 mb-4 xxs:mb-0">
              <p className="text-black xxs:text-lg 2md:text-1.5xl font-bold mb-2">
                Where Are You in Your Journey?
              </p>
              <p className="text-black xxs:text-sm 2md:text-base font-normal">
                No matter what stage you&lsquo;re in, it&lsquo;s never too early
                or too late to optimize your retirement plan.
              </p>
            </div>

            <Link
              href="tel:+91-9660032889"
              className="bg-white block w-fit text-center rounded-full py-2 xxs:py-4 px-5 2md:px-9 text-black text-sm m-auto xxs:m-0 xxs:text-base 2md:text-lg font-bold"
            >
              Talk to our Expert
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
